import {isOpnChargeGated} from '../../../api/provider-status/helpers'
import {ProviderHealthState} from '../../../api/provider-status/types'
import {PAYMENT_PROVIDER_OPN} from '../../../constants/payments'
import {PaymentTypes} from '../hooks/payment-types'

/*
 * Integration-health checkout gate (exsportia-product#80). When Opn's `charge` op reads DOWN, no
 * Opn charge may leave the app: saved card (PAY_NOW) and the Opn widget (ONE_TIME: card, PromptPay,
 * wallets) are disabled; pay-on-location and free checkouts stay. Unknown status = no gate.
 */

export const OPN_PAYMENT_TYPES: PaymentTypes[] = [PaymentTypes.PAY_NOW, PaymentTypes.ONE_TIME]

export const isOpnPaymentType = (paymentType: PaymentTypes | null | undefined): boolean =>
  Boolean(paymentType && OPN_PAYMENT_TYPES.includes(paymentType))

export type CheckoutGate = {
  /** Show the gate copy. */
  isGated: boolean
  /** A non-Opn method (pay on location) is still on offer: show the second line. */
  hasOtherMethod: boolean
  /** The selected method goes through Opn: its submit is disabled. */
  isSelectionBlocked: boolean
}

export const NO_CHECKOUT_GATE: CheckoutGate = {isGated: false, hasOtherMethod: false, isSelectionBlocked: false}

export type ResolveCheckoutGateArgs = {
  provider?: string | null
  chargeState: ProviderHealthState | null | undefined
  totalPrice?: number | null
  allowedOptions: PaymentTypes[]
  paymentType: PaymentTypes | null | undefined
}

export const resolveCheckoutGate = ({
  provider,
  chargeState,
  totalPrice,
  allowedOptions,
  paymentType,
}: ResolveCheckoutGateArgs): CheckoutGate => {
  if (!isOpnChargeGated({provider, chargeState, totalPrice})) {
    return NO_CHECKOUT_GATE
  }
  return {
    isGated: true,
    hasOtherMethod: allowedOptions.some((option) => !isOpnPaymentType(option)),
    isSelectionBlocked: isOpnPaymentType(paymentType),
  }
}

export type GuardChargeSubmitArgs = {
  provider?: string | null
  totalPrice?: number | null
  paymentType: PaymentTypes | null | undefined
  readChargeState: () => Promise<ProviderHealthState | null>
  submit: () => void
  onBlocked: () => void
}

/**
 * The submit-side guard: right before an Opn charge path is submitted, read the (60 s cached)
 * status once more. DOWN → the charge never leaves the app. A read that fails is unknown → submit.
 */
export const guardChargeSubmit = async ({
  provider,
  totalPrice,
  paymentType,
  readChargeState,
  submit,
  onBlocked,
}: GuardChargeSubmitArgs): Promise<boolean> => {
  if (!isOpnPaymentType(paymentType) || !isOpnChargeGated({provider, chargeState: 'DOWN', totalPrice})) {
    submit()
    return true
  }
  const chargeState = await readChargeState().catch(() => null)
  if (isOpnChargeGated({provider, chargeState, totalPrice})) {
    onBlocked()
    return false
  }
  submit()
  return true
}

/** DEGRADED + a failed charge: the existing copy gains the hint, never the provider's own text. */
export const withChargeRetryHint = (message: string, hint: string, isDegraded: boolean): string =>
  isDegraded ? `${message} ${hint}` : message

/**
 * The provider the gate checks for a venue. `GET payments/terminalName` returns the charge
 * (non-tokenized) terminal; saved-card charges go through the venue's tokenized twin, which the
 * server creates with the same provider (`post.create-terminal.controller.js`) and which no client
 * route exposes. While the terminal is unknown (loading, failed) the venue is treated as Opn, so an
 * Opn outage blocks rather than lets a saved-card charge through. Only matters when charge is DOWN.
 */
export const resolveGateProvider = (terminal: {provider?: string | null} | null | undefined): string =>
  terminal?.provider || PAYMENT_PROVIDER_OPN

/** One run at a time: a second call while the first is still reading the status is dropped. */
export const createSingleFlight = () => {
  let running = false
  return (task: () => Promise<unknown>): Promise<boolean> => {
    if (running) {
      return Promise.resolve(false)
    }
    running = true
    return task()
      .then(() => true)
      .finally(() => {
        running = false
      })
  }
}

/** Opn widget: do not open it when the cached charge state is already DOWN. */
export const isOpnWidgetBlocked = (provider: string | null | undefined, cachedChargeState: ProviderHealthState | null): boolean =>
  isOpnChargeGated({provider, chargeState: cachedChargeState})

export type GuardOpnWidgetChargeArgs = {
  totalPrice?: number | null
  readChargeState: () => Promise<ProviderHealthState | null>
  charge: () => void
  onBlocked: () => void
}

/** Opn widget token → `payments/opn/charge`: re-read right before; DOWN → the charge is dropped. */
export const guardOpnWidgetCharge = ({totalPrice, readChargeState, charge, onBlocked}: GuardOpnWidgetChargeArgs) =>
  guardChargeSubmit({
    provider: PAYMENT_PROVIDER_OPN,
    paymentType: PaymentTypes.ONE_TIME,
    totalPrice,
    readChargeState,
    submit: charge,
    onBlocked,
  })

type CycleLike = {isPaid?: boolean | null; payDate?: number | null; startDate?: number | null}

/**
 * Does saving a new card on this pass charge right away? `PUT memberships/:id/changePaymentMethod`
 * stores the card and then runs `paySubscriptionCycles`, which charges every cycle
 * `markCyclesToPay` marks (exsportia-server memberships/helpers/pay-current-cycle.helpers.js): the
 * unpaid cycles up to the last unpaid one already started, and an unpaid first cycle even before it
 * starts. Mirrored here (no payAhead) so a plain card swap with nothing due stays ungated.
 */
export const hasCycleDueOnCardChange = (cycles: CycleLike[] | null | undefined, now: number = Date.now()): boolean => {
  const list = cycles || []
  const isUnpaid = (cycle: CycleLike) => cycle.isPaid !== true
  let lastDueIndex = -1
  list.forEach((cycle, index) => {
    const dueFrom = cycle.payDate || cycle.startDate
    if (isUnpaid(cycle) && typeof dueFrom === 'number' && now > dueFrom) {
      lastDueIndex = index
    }
  })
  const reach = lastDueIndex === -1 ? 0 : lastDueIndex
  return list.some((cycle, index) => index <= reach && isUnpaid(cycle))
}
